import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CarouselSlide from './CarouselSlide';
import CarouselControls from './CarouselControls';
import { useCarousel } from '../../hooks/useCarousel';

const Carousel = ({ 
  images, 
  autoPlay = true, 
  interval = 4000,
  showThumbnails = true,
  showArrows = true,
  showDots = true,
}) => {
  const {
    currentIndex,
    setCurrentIndex,
    isPlaying,
    setIsPlaying,
    goToNext,
    goToPrevious,
    goToSlide,
  } = useCarousel(images.length, autoPlay, interval);

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef(null);

  // Minimum swipe distance
  const minSwipeDistance = 50;

  // Handle touch start
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  // Handle touch move
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  // Handle touch end
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      goToNext();
    }
    if (isRightSwipe) {
      goToPrevious();
    }
  };

  // Pause on hover
  const handleMouseEnter = () => {
    setIsPaused(true);
    setIsPlaying(false);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
    if (autoPlay) {
      setIsPlaying(true);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === ' ') {
        e.preventDefault();
        setIsPlaying(!isPlaying);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (container) {
        container.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [goToNext, goToPrevious, isPlaying]);

  if (!images || images.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-accent-blue/10 rounded-2xl">
        <div className="text-center">
          <div className="text-6xl mb-4">📷</div>
          <p className="text-neutral-gray-dark">No images to display</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-2xl shadow-2xl focus:outline-none"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      tabIndex={0}
      role="region"
      aria-label="Image carousel"
    >
      {/* Main Slide Container */}
      <div className="relative aspect-video bg-neutral-gray overflow-hidden">
        <AnimatePresence mode="wait">
          <CarouselSlide
            key={currentIndex}
            image={images[currentIndex]}
            index={currentIndex}
          />
        </AnimatePresence>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />

        {/* Navigation Arrows */}
        {showArrows && images.length > 1 && (
          <CarouselControls
            onPrevious={goToPrevious}
            onNext={goToNext}
            isPlaying={isPlaying}
            onTogglePlay={() => setIsPlaying(!isPlaying)}
            showPlayPause={autoPlay}
          />
        )}

        {/* Slide Counter */}
        <div className="absolute top-4 right-4 bg-black/50 text-white px-4 py-2 rounded-full font-display text-sm backdrop-blur-sm">
          {currentIndex + 1} / {images.length}
        </div>

        {/* Progress Bar */}
        {autoPlay && isPlaying && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
            <motion.div
              key={currentIndex}
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ duration: interval / 1000, ease: 'linear' }}
              className="h-full bg-accent-orange"
            />
          </div>
        )}
      </div>

      {/* Dots Navigation */}
      {showDots && images.length > 1 && (
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex gap-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-white scale-125'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Thumbnails */}
      {showThumbnails && images.length > 1 && (
        <div className="bg-black/80 p-4">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden transition-all duration-300 ${
                  index === currentIndex
                    ? 'ring-2 ring-accent-orange scale-105'
                    : 'opacity-60 hover:opacity-100'
                }`}
              >
                <img
                  src={image.src}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Pause Indicator */}
      <AnimatePresence>
        {isPaused && !isPlaying && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black/50 p-6 rounded-full backdrop-blur-sm"
          >
            <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Carousel;